import {
  Form,
  Input,
  Select,
  Upload,
  Button,
  Progress,
  Typography,
  Divider,
  Spin,
  Popconfirm,
  Tabs,
  Checkbox,
  App,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import "./resourceForm.scss";
import {
  getResourceTypeOptions,
  parseDropdownValue,
  toDropdownValue,
  type ResourceTypeDropdownValue,
} from "../LessonForm/utils";
import {
  initialValuesResource,
  buildFilesArray,
  buildFilesArrayForUpdate,
  buildReadingBlockDto,
  type PdfFileItem,
} from "./utils";
import { type ResourceEntityInput } from "../../../services/resources/resources.types";
import type {
  WorksheetBlockDto,
  WorksheetFileType,
} from "../../../services/resources/resources.types";
import {
  customUpload,
  handleSubmitFormResponseManage,
  uploadGameArchive,
} from "../../../utils";
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  MAX_UPLOAD_IMAGE_SIZE_BYTES,
} from "../../../utils/imageOptimizer";
import {
  createResource,
  updateResource,
} from "../../../services/resources/resources.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { resolveEntityId } from "../../../utils/route-entity-id";
import { useDispatch } from "react-redux";
import { useResources } from "../../../hooks/useResource";
import { useNotification } from "../../../hooks/useNotification";
import ActivityIndicator from "../../ActivityIndicator";
import ResourcePreview from "../../common/ResourcePreview";
import { useGameQuizzesSelector } from "../../../hooks/useGameQuizzesSelector";
import { ResourceType, type ReadingVariant } from "../../../types";
import ReadingContentEditor from "../../ReadingContentEditor/ReadingContentEditor";
import SlidesGallery from "./SlidesGallery";
import ExtractionTab from "./ExtractionTab";
import ScriptExtractionTab from "./ScriptExtractionTab";
import EntityHistory from "../../EntityHistory";
import LastModified from "../../LastModified";
import type {
  LatestExtractionJob,
  ResourceFileResponse,
  ResourceScriptExtraction,
} from "../../../types/api/getResources";
import type {
  ReadingContentDraft,
  ReadingContent,
} from "../../../services/reading-content/reading-content.types";
import {
  getReadingContentByResourceId,
  createReadingContent,
  updateReadingContent as updateReadingContentApi,
  addBlock,
  updateBlock,
  deleteBlock,
  reorderBlocks,
} from "../../../services/reading-content/reading-content.service";

const { Option } = Select;
const { Title } = Typography;

// ── LITERACY_WORKSHEET UI types ──────────────────────────────────────────────
interface WorksheetFileUiItem {
  fileType: WorksheetFileType;
  label: string;
  file_id: string;
  url: string | null;
  uploading: boolean;
  progress: number;
  dbId?: number; // existing record id (kept for reference but not sent to API)
}

interface WorksheetBlockUiItem {
  uiId: string; // local key, never sent to API
  id?: number; // backend DB id
  title: string;
  files: WorksheetFileUiItem[];
}

const WORKSHEET_FILE_SLOTS: { fileType: WorksheetFileType; label: string }[] = [
  { fileType: "PRE_TRACING", label: "Pretrazo" },
  { fileType: "SCRIPT", label: "Script" },
  { fileType: "CURSIVE", label: "Cursiva" },
];

const emptyWorksheetBlock = (): WorksheetBlockUiItem => ({
  uiId: crypto.randomUUID(),
  title: "",
  files: WORKSHEET_FILE_SLOTS.map((s) => ({
    fileType: s.fileType,
    label: s.label,
    file_id: "",
    url: null,
    uploading: false,
    progress: 0,
  })),
});
// ────────────────────────────────────────────────────────────────────────────

// PDF Item Component
interface PdfItemProps {
  pdf: PdfFileItem;
  index: number;
  onUpload: (pdfId: string, options: any) => void;
  onRemove: (pdfId: string) => void;
  progress: number;
  title?: string;
}

const PdfItem = ({
  pdf,
  index,
  onUpload,
  onRemove,
  progress,
  title,
}: PdfItemProps) => {
  const style = {
    marginBottom: "16px",
    padding: "12px",
    border: "1px solid var(--bo-border-strong)",
    borderRadius: "8px",
    backgroundColor: "var(--bo-card-bg)",
  };

  return (
    <div style={style}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <strong>{title ?? `Archivo PDF ${index + 1}`}</strong>
      </div>

      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Upload
          customRequest={(options) => onUpload(pdf.id, options)}
          showUploadList={false}
          disabled={pdf.uploading}
          accept=".pdf"
        >
          <Button icon={<UploadOutlined />} loading={pdf.uploading}>
            {pdf.file_id ? "Reemplazar archivo" : "Subir archivo"}
          </Button>
        </Upload>

        <Popconfirm
          title="¿Estás seguro de eliminar este PDF?"
          onConfirm={() => onRemove(pdf.id)}
          okText="Sí, eliminar"
          cancelText="Cancelar"
        >
          <Button danger icon={<DeleteOutlined />} disabled={pdf.uploading}>
            Eliminar
          </Button>
        </Popconfirm>
      </div>

      {pdf.uploading && (
        <Progress
          percent={progress}
          size="small"
          style={{ marginTop: "8px" }}
        />
      )}

      {pdf.file_id && !pdf.uploading && (
        <p
          className="uploaded-filename"
          style={{ marginTop: "8px", marginBottom: "8px" }}
        >
          📄 {pdf.file_id}
        </p>
      )}

      {pdf.url && pdf.file_id && (
        <>
          <ResourcePreview
            fileId={pdf.file_id}
            fileUrl={pdf.url}
            type={ResourceType.DOWNLOAD}
          />
        </>
      )}
    </div>
  );
};

const ResourceForm = () => {
  const { message } = App.useApp();
  const location = useLocation();
  const { getResourceById } = useResources();
  const { loading: loadingGameQuizzes, gameQuizzes } = useGameQuizzesSelector();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const { id: idParam } = useParams();
  // URL param first so deep links (content map, refresh, shared URLs) work;
  // navigation state is the legacy fallback used by the resources list.
  const resourceId = resolveEntityId(idParam, location?.state);
  const [initialValues, setinitialValues] = useState<ResourceEntityInput>(
    initialValuesResource,
  );
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);

  const [loading, setLoading] = useState(false);

  // file_id
  const [uploading, setUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState("");
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [optimizedFileUrl, setOptimizedFileUrl] = useState<string | null>(null);

  // cover
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadedCover, setUploadedCover] = useState("");
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  // extra_file_id
  const [extraFileUploading, setExtraFileUploading] = useState(false);
  const [uploadedExtraFile, setUploadedExtraFile] = useState("");
  const [extraFileUrl, setExtraFileUrl] = useState<string | null>(null);

  // additional_extra_file_id
  const [additionalExtraFileUploading, setAdditionalExtraFileUploading] =
    useState(false);
  const [uploadedAdditionalExtraFile, setUploadedAdditionalExtraFile] =
    useState("");
  const [additionalExtraFileUrl, setAdditionalExtraFileUrl] = useState<
    string | null
  >(null);

  // student_book_file_id
  const [studentBookUploading, setStudentBookUploading] = useState(false);
  const [uploadedStudentBook, setUploadedStudentBook] = useState("");
  const [studentBookUrl, setStudentBookUrl] = useState<string | null>(null);

  // diagnostic_teacher_file_id / diagnostic_student_file_id (DIAGNOSTIC)
  const [diagnosticTeacherUploading, setDiagnosticTeacherUploading] =
    useState(false);
  const [uploadedDiagnosticTeacher, setUploadedDiagnosticTeacher] =
    useState("");
  const [diagnosticTeacherUrl, setDiagnosticTeacherUrl] = useState<
    string | null
  >(null);
  const [diagnosticStudentUploading, setDiagnosticStudentUploading] =
    useState(false);
  const [uploadedDiagnosticStudent, setUploadedDiagnosticStudent] =
    useState("");
  const [diagnosticStudentUrl, setDiagnosticStudentUrl] = useState<
    string | null
  >(null);
  // diagnostic_student_editable_file_id: optional Word copy of the student exam
  const [
    diagnosticStudentEditableUploading,
    setDiagnosticStudentEditableUploading,
  ] = useState(false);
  const [
    uploadedDiagnosticStudentEditable,
    setUploadedDiagnosticStudentEditable,
  ] = useState("");
  const [diagnosticStudentEditableUrl, setDiagnosticStudentEditableUrl] =
    useState<string | null>(null);

  // file_id_ios (versión iOS del juego)
  const [uploadingIos, setUploadingIos] = useState(false);
  const [uploadedFilenameIos, setUploadedFilenameIos] = useState("");
  const [fileUrlIos, setFileUrlIos] = useState<string | null>(null);
  const [progressIos, setProgressIos] = useState(0);
  const [processingIos, setProcessingIos] = useState(false);

  // PDF files for DOWNLOAD type
  const [pdfFiles, setPdfFiles] = useState<PdfFileItem[]>([]);
  // picture card single file
  const [pictureCard, setPictureCard] = useState<PdfFileItem>({
    id: crypto.randomUUID(),
    file_id: "",
    url: null,
    uploading: false,
    progress: 0,
  });

  // worksheet blocks for LITERACY_WORKSHEET type
  const [worksheetBlocks, setWorksheetBlocks] = useState<
    WorksheetBlockUiItem[]
  >([]);

  // Reading variant state (UI-only: kinder = PDF upload, primaria = block editor)
  const [readingVariant, setReadingVariant] = useState<ReadingVariant | null>(
    null,
  );

  // Reading content draft state
  const readingContentDraftRef = useRef<ReadingContentDraft | null>(null);
  const [existingReadingContent, setExistingReadingContent] =
    useState<ReadingContent | null>(null);

  const handleReadingDataChange = (data: ReadingContentDraft) => {
    readingContentDraftRef.current = data;
  };

  // PDF extraction state (read-only — surfaced in "Extracción" tab)
  const [extractionFiles, setExtractionFiles] = useState<
    ResourceFileResponse[] | undefined
  >(undefined);
  const [extractionJob, setExtractionJob] = useState<
    LatestExtractionJob | null | undefined
  >(undefined);

  // Script extraction state (read-only — surfaced in "Extracción de Script" tab)
  const [scriptExtraction, setScriptExtraction] =
    useState<ResourceScriptExtraction | null>(null);

  // Store existing file IDs for UPDATE operations
  const [existingFileIds, setExistingFileIds] = useState<{
    file_id_db_id?: number;
    file_id_ios_db_id?: number;
    student_book_db_id?: number;
    extra_file_db_id?: number;
    additional_extra_file_db_id?: number;
    picture_card_db_id?: number;
    diagnostic_teacher_db_id?: number;
    diagnostic_student_db_id?: number;
    diagnostic_student_editable_db_id?: number;
  }>({});

  const fetchInfo = async () => {
    setLoading(true);
    if (resourceId) {
      try {
        const data = await getResourceById(resourceId);
        if (data) {
          // Capture extraction data for the read-only "Extracción" tab.
          setExtractionFiles(data.files);
          setExtractionJob(data.latest_extraction_job ?? null);
          setScriptExtraction(data.script_extraction ?? null);

          setinitialValues({
            title: data.title,
            type: data.type,
            file_id: data.file_id,
            file_id_ios: data.file_id_ios || "",
            extra_file_id: data.extra_file_id,
            additional_extra_file_id: data.additional_extra_file_id,
            student_book_file_id: data.student_book_file_id || "",
            diagnostic_teacher_file_id: data.diagnostic_teacher_file_id || "",
            diagnostic_student_file_id: data.diagnostic_student_file_id || "",
            diagnostic_student_editable_file_id:
              data.diagnostic_student_editable_file_id || "",
            extra_file_id_related_field: data.extra_file_id_related_field || "",
            additional_extra_file_id_related_field:
              data.additional_extra_file_id_related_field || "",
            additional_info: data.additional_info,
            cover: data.cover,
            is_secondary: data.is_secondary ?? false,
            game_quiz_id: data.game_quiz_id ?? undefined,
            genially_edit_url: data.genially_edit_url || "",
            script_doc_url: data.script_doc_url ?? "",
            drive_url: data.drive_url ?? "",
          });

          // images
          setUploadedFilename(data.file_id);
          setUploadedCover(data.cover);
          setUploadedExtraFile(data.extra_file_id);
          setUploadedAdditionalExtraFile(data.additional_extra_file_id);
          setUploadedStudentBook(data.student_book_file_id || "");
          setUploadedDiagnosticTeacher(data.diagnostic_teacher_file_id || "");
          setUploadedDiagnosticStudent(data.diagnostic_student_file_id || "");
          setUploadedDiagnosticStudentEditable(
            data.diagnostic_student_editable_file_id || "",
          );

          // iOS game file
          if (data.file_id_ios) {
            setUploadedFilenameIos(data.file_id_ios);
          }
          if (data.file_id_ios_url) {
            setFileUrlIos(data.file_id_ios_url);
          }

          // url
          data?.file_id_url && setFileUrl(data?.file_id_url);
          data?.cover_url && setCoverUrl(data?.cover_url);
          data?.extra_file_id_url && setExtraFileUrl(data?.extra_file_id_url);
          data?.additional_extra_file_id_url &&
            setAdditionalExtraFileUrl(data?.additional_extra_file_id_url);
          data?.student_book_file_id_url &&
            setStudentBookUrl(data?.student_book_file_id_url);
          data?.diagnostic_teacher_file_id_url &&
            setDiagnosticTeacherUrl(data?.diagnostic_teacher_file_id_url);
          data?.diagnostic_student_file_id_url &&
            setDiagnosticStudentUrl(data?.diagnostic_student_file_id_url);
          data?.diagnostic_student_editable_file_id_url &&
            setDiagnosticStudentEditableUrl(
              data?.diagnostic_student_editable_file_id_url,
            );

          // Load PDF files for DOWNLOAD type
          if (data.type === ResourceType.DOWNLOAD) {
            const filesData = (data as any).files;
            if (filesData && Array.isArray(filesData)) {
              const pdfs: PdfFileItem[] = filesData
                .filter((file: any) => file.file_type === "MAIN_FILE")
                .map((file: any) => ({
                  id: crypto.randomUUID(),
                  file_id: file.file_id,
                  url: file.file_id_url || null,
                  uploading: false,
                  progress: 0,
                  dbId: file.id, // Store database ID for PATCH operations
                }));
              setPdfFiles(pdfs);

              // picture card if present
              const picture = filesData.find(
                (file: any) => file.file_type === "PICTURE_CARD",
              );
              if (picture) {
                setPictureCard({
                  id: crypto.randomUUID(),
                  file_id: picture.file_id,
                  url: picture.file_id_url || null,
                  uploading: false,
                  progress: 0,
                  dbId: picture.id,
                });
                setExistingFileIds((prev) => ({
                  ...prev,
                  picture_card_db_id: picture.id,
                }));
                setinitialValues((prev) => ({
                  ...prev,
                  picture_card_file_id: picture.file_id,
                }));
              }
            }
          }

          // Load existing reading content for READING type and detect variant
          if (data.type === ResourceType.READING) {
            try {
              const readingData =
                await getReadingContentByResourceId(resourceId);
              if (readingData) {
                setExistingReadingContent(readingData);
                setReadingVariant("primaria");
              } else {
                setReadingVariant("kinder");
              }
            } catch (err) {
              console.error("Error loading reading content:", err);
              // If fetch fails, assume kinder (no reading content)
              setReadingVariant("kinder");
            }
          }

          // Load worksheet blocks for LITERACY_WORKSHEET type
          if (data.type === ResourceType.LITERACY_WORKSHEET) {
            const rawBlocks = data.worksheetBlocks ?? [];
            const uiBlocks: WorksheetBlockUiItem[] = rawBlocks.map((block) => ({
              uiId: crypto.randomUUID(),
              id: block.id,
              title: block.title,
              files: WORKSHEET_FILE_SLOTS.map((slot) => {
                const existing = block.files.find(
                  (f) => f.file_type === slot.fileType,
                );
                return {
                  fileType: slot.fileType,
                  label: slot.label,
                  file_id: existing?.file_id ?? "",
                  url: existing?.file_id_url ?? null,
                  uploading: false,
                  progress: 0,
                  dbId: existing?.id,
                };
              }),
            }));
            setWorksheetBlocks(
              uiBlocks.length > 0 ? uiBlocks : [emptyWorksheetBlock()],
            );
          }

          // Store existing file IDs from backend for other resource types
          if (data.type !== ResourceType.DOWNLOAD && (data as any).files) {
            const filesData = (data as any).files;
            const fileIds: any = {};

            // Mapeo específico según el tipo de recurso
            if (data.type === ResourceType.AUDIO) {
              filesData.forEach((file: any) => {
                if (file.file_type === "AUDIO_TRACK") {
                  fileIds.file_id_db_id = file.id; // Archivo principal de audio
                } else if (file.file_type === "TRANSCRIPT") {
                  fileIds.extra_file_db_id = file.id; // Transcripción
                }
              });
            } else if (data.type === ResourceType.BOOK) {
              filesData.forEach((file: any) => {
                if (file.file_type === "TEACHER_BOOK") {
                  fileIds.file_id_db_id = file.id; // Libro maestro
                } else if (file.file_type === "STUDENT_BOOK") {
                  fileIds.student_book_db_id = file.id; // Libro alumno
                } else if (file.file_type === "AUDIO_TRACK") {
                  // Para BOOK, puede haber 2 audios
                  if (!fileIds.extra_file_db_id) {
                    fileIds.extra_file_db_id = file.id; // Primer audio
                  } else {
                    fileIds.additional_extra_file_db_id = file.id; // Segundo audio
                  }
                }
              });
            } else if (data.type === ResourceType.GAME_QUIZ) {
              filesData.forEach((file: any) => {
                if (file.file_type === "SUPPLEMENTARY") {
                  fileIds.file_id_db_id = file.id; // Video opcional de la actividad de juego
                }
              });
            } else if (data.type === ResourceType.DIAGNOSTIC) {
              filesData.forEach((file: any) => {
                if (file.file_type === "DIAGNOSTIC_TEACHER") {
                  fileIds.diagnostic_teacher_db_id = file.id; // Examen versión maestra
                } else if (file.file_type === "DIAGNOSTIC_STUDENT") {
                  fileIds.diagnostic_student_db_id = file.id; // Examen versión alumno
                } else if (file.file_type === "DIAGNOSTIC_STUDENT_EDITABLE") {
                  fileIds.diagnostic_student_editable_db_id = file.id; // Examen versión alumno en Word
                }
              });
            } else {
              // Para otros tipos (VIDEO, GAME, etc.) que usan MAIN_FILE
              filesData.forEach((file: any) => {
                if (file.file_type === "MAIN_FILE") {
                  fileIds.file_id_db_id = file.id;
                } else if (file.file_type === "MAIN_FILE_IOS") {
                  fileIds.file_id_ios_db_id = file.id;
                }
              });
            }

            setExistingFileIds(fileIds);
          }
        }
      } catch (error) {
        console.error("Error al obtener el recurso:", error);
        notify(
          "error",
          "No se pudo cargar la información del recurso.",
          "Error",
        );
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInfo();
  }, [resourceId]);

  // Lightweight refresh used by ExtractionTab polling — only updates extraction
  // state, never mutates form fields (otherwise we'd clobber unsaved edits).
  const refetchExtraction = async () => {
    if (!resourceId) return;
    try {
      const data = await getResourceById(resourceId);
      if (data) {
        setExtractionFiles(data.files);
        setExtractionJob(data.latest_extraction_job ?? null);
        setScriptExtraction(data.script_extraction ?? null);
      }
    } catch (error) {
      console.error("refetchExtraction error", error);
    }
  };

  // Detectar versión optimizada del juego (si existe)
  useEffect(() => {
    const detectOptimized = async () => {
      setOptimizedFileUrl(null);
      if (!fileUrl || initialValues.type !== ResourceType.GAME) return;

      // Probar rutas comunes donde podría existir una versión optimizada
      const candidates = [
        fileUrl.replace(/index\.html$/i, "optimized/index.html"),
        fileUrl.replace(/index\.html$/i, "mobile/index.html"),
        fileUrl.replace(/index\.html$/i, "optimized/index.htm"),
      ];

      for (const candidate of candidates) {
        try {
          const resp = await fetch(candidate, { method: "HEAD" });
          if (resp.ok) {
            setOptimizedFileUrl(candidate);
            return;
          }
        } catch (err) {
          // Ignorar errores de CORS / red y probar siguiente candidate
        }
      }
    };

    void detectOptimized();
  }, [fileUrl, initialValues.type]);

  useEffect(() => {
    if (
      initialValues.type === ResourceType.DOWNLOAD &&
      pdfFiles.length === 0 &&
      !resourceId
    ) {
      setPdfFiles([
        { id: crypto.randomUUID(), file_id: "", url: null, uploading: false },
      ]);
    }
  }, [initialValues.type, resourceId, pdfFiles.length]);

  useEffect(() => {
    if (
      initialValues.type === ResourceType.LITERACY_WORKSHEET &&
      worksheetBlocks.length === 0 &&
      !resourceId
    ) {
      setWorksheetBlocks([emptyWorksheetBlock()]);
    }
  }, [initialValues.type, resourceId, worksheetBlocks.length]);

  const handleCustomUpload = async (
    options: any,
    field:
      | "file_id"
      | "cover"
      | "extra_file_id"
      | "additional_extra_file_id"
      | "student_book_file_id"
      | "diagnostic_teacher_file_id"
      | "diagnostic_student_file_id"
      | "diagnostic_student_editable_file_id",
  ) => {
    console.log(`Iniciando handleCustomUpload para ${field}`);

    if (field === "file_id") {
      setUploading(true);
    } else if (field === "cover") {
      setUploadingCover(true);
    } else if (field === "extra_file_id") {
      setExtraFileUploading(true);
    } else if (field === "additional_extra_file_id") {
      setAdditionalExtraFileUploading(true);
    } else if (field === "student_book_file_id") {
      setStudentBookUploading(true);
    } else if (field === "diagnostic_teacher_file_id") {
      setDiagnosticTeacherUploading(true);
    } else if (field === "diagnostic_student_file_id") {
      setDiagnosticStudentUploading(true);
    } else if (field === "diagnostic_student_editable_file_id") {
      setDiagnosticStudentEditableUploading(true);
    }
    setProgress(0);

    const optionsWithProgress = {
      ...options,
      onProgress: ({ percent }: { percent: number }) => {
        setProgress(Math.round(percent));
        if (options.onProgress) {
          options.onProgress({ percent });
        }
      },
      ...(initialValues.type === ResourceType.GAME && field === "file_id"
        ? { setProcessing }
        : {}),
    };

    try {
      console.log(`Llamando a customUpload para ${field}`);

      let result;

      //new function for games upload
      if (initialValues.type === ResourceType.GAME && field === "file_id") {
        result = await uploadGameArchive(optionsWithProgress);
        console.log("Resultado de uploadGameArchive:", result);
      } else {
        result = await customUpload(optionsWithProgress);
        console.log("Resultado de customUpload:", result);
      }

      const isGame = initialValues.type === ResourceType.GAME;

      if (
        (isGame &&
          field === "file_id" &&
          "gameEntryPoint" in result &&
          result?.gameEntryPoint) ||
        (!isGame &&
          field === "file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "cover" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "extra_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "additional_extra_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "student_book_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "diagnostic_teacher_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "diagnostic_student_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url) ||
        (field === "diagnostic_student_editable_file_id" &&
          "fileName" in result &&
          result?.fileName &&
          result?.url)
      ) {
        const fileValue =
          isGame && field === "file_id" && "gameEntryPoint" in result
            ? result.gameEntryPoint
            : "fileName" in result
              ? result.fileName
              : "";
        const urlValue =
          isGame && field === "file_id" && "gameEntryPoint" in result
            ? result.gameEntryPoint
            : "url" in result
              ? result.url
              : "";

        if (field === "file_id") {
          setinitialValues({
            ...initialValues,
            file_id: fileValue,
          });
          setUploadedFilename(fileValue);
          setFileUrl(urlValue);
        } else if (field === "cover") {
          setinitialValues({
            ...initialValues,
            cover: fileValue,
          });
          setUploadedCover(fileValue);
          setCoverUrl(urlValue);
        } else if (field === "extra_file_id") {
          setinitialValues({
            ...initialValues,
            extra_file_id: fileValue,
          });
          setUploadedExtraFile(fileValue);
          setExtraFileUrl(urlValue);
        } else if (field === "additional_extra_file_id") {
          setinitialValues({
            ...initialValues,
            additional_extra_file_id: fileValue,
          });
          setUploadedAdditionalExtraFile(fileValue);
          setAdditionalExtraFileUrl(urlValue);
        } else if (field === "student_book_file_id") {
          setinitialValues({
            ...initialValues,
            student_book_file_id: fileValue,
          });
          setUploadedStudentBook(fileValue);
          setStudentBookUrl(urlValue);
        } else if (field === "diagnostic_teacher_file_id") {
          setinitialValues({
            ...initialValues,
            diagnostic_teacher_file_id: fileValue,
          });
          setUploadedDiagnosticTeacher(fileValue);
          setDiagnosticTeacherUrl(urlValue);
        } else if (field === "diagnostic_student_file_id") {
          setinitialValues({
            ...initialValues,
            diagnostic_student_file_id: fileValue,
          });
          setUploadedDiagnosticStudent(fileValue);
          setDiagnosticStudentUrl(urlValue);
        } else if (field === "diagnostic_student_editable_file_id") {
          setinitialValues({
            ...initialValues,
            diagnostic_student_editable_file_id: fileValue,
          });
          setUploadedDiagnosticStudentEditable(fileValue);
          setDiagnosticStudentEditableUrl(urlValue);
        }
      } else {
        console.warn(`Resultado incompleto para ${field}:`, result);
      }
    } catch (error) {
      console.error(`Error en la subida personalizada para ${field}:`, error);
    } finally {
      if (field === "file_id") {
        setUploading(false);
      } else if (field === "cover") {
        setUploadingCover(false);
      } else if (field === "extra_file_id") {
        setExtraFileUploading(false);
      } else if (field === "additional_extra_file_id") {
        setAdditionalExtraFileUploading(false);
      } else if (field === "student_book_file_id") {
        setStudentBookUploading(false);
      } else if (field === "diagnostic_teacher_file_id") {
        setDiagnosticTeacherUploading(false);
      } else if (field === "diagnostic_student_file_id") {
        setDiagnosticStudentUploading(false);
      } else if (field === "diagnostic_student_editable_file_id") {
        setDiagnosticStudentEditableUploading(false);
      }
      console.log(`Finalizado handleCustomUpload para ${field}`);
    }
  };

  // PDF handlers for DOWNLOAD type
  const handleAddPdf = () => {
    setPdfFiles([
      ...pdfFiles,
      {
        id: crypto.randomUUID(),
        file_id: "",
        url: null,
        uploading: false,
        progress: 0,
      },
    ]);
  };

  const handleRemovePdf = (pdfId: string) => {
    setPdfFiles((prev) => prev.filter((p) => p.id !== pdfId));
  };

  const handlePdfUpload = async (pdfId: string, options: any) => {
    // Marcar como uploading y resetear progreso para ese PDF
    setPdfFiles((prevFiles) =>
      prevFiles.map((p) =>
        p.id === pdfId ? { ...p, uploading: true, progress: 0 } : p,
      ),
    );

    const optionsWithProgress = {
      ...options,
      onProgress: ({ percent }: { percent: number }) => {
        setPdfFiles((prevFiles) =>
          prevFiles.map((p) =>
            p.id === pdfId ? { ...p, progress: Math.round(percent) } : p,
          ),
        );
        if (options.onProgress) {
          options.onProgress({ percent });
        }
      },
    };

    try {
      const result = await customUpload(optionsWithProgress);
      if ("fileName" in result && result?.fileName && result?.url) {
        // Actualizar con datos del archivo subido
        setPdfFiles((prevFiles) =>
          prevFiles.map((p) =>
            p.id === pdfId
              ? {
                  ...p,
                  file_id: result.fileName,
                  url: result.url,
                  uploading: false,
                  progress: 100,
                }
              : p,
          ),
        );
      } else {
        // Si no hay resultado válido, marcar como no uploading
        setPdfFiles((prevFiles) =>
          prevFiles.map((p) =>
            p.id === pdfId ? { ...p, uploading: false, progress: 0 } : p,
          ),
        );
      }
    } catch (error) {
      console.error(`Error uploading PDF ${pdfId}:`, error);
      setPdfFiles((prevFiles) =>
        prevFiles.map((p) =>
          p.id === pdfId ? { ...p, uploading: false, progress: 0 } : p,
        ),
      );
    }
  };

  // Handler to upload picture card PDF for DOWNLOAD type
  const handlePictureCardUpload = async (options: any) => {
    setPictureCard((prev) => ({ ...prev, uploading: true, progress: 0 }));
    const optionsWithProgress = {
      ...options,
      onProgress: ({ percent }: { percent: number }) => {
        setPictureCard((prev) => ({ ...prev, progress: Math.round(percent) }));
        if (options.onProgress) {
          options.onProgress({ percent });
        }
      },
    };
    try {
      const result = await customUpload(optionsWithProgress);
      if ("fileName" in result && result?.fileName && result?.url) {
        setPictureCard((prev) => ({
          ...prev,
          file_id: result.fileName,
          url: result.url,
          uploading: false,
          progress: 100,
        }));
      } else {
        setPictureCard((prev) => ({ ...prev, uploading: false, progress: 0 }));
      }
    } catch (error) {
      console.error("Error uploading picture card:", error);
      setPictureCard((prev) => ({ ...prev, uploading: false, progress: 0 }));
    }
  };

  const handleRemovePictureCard = () => {
    setPictureCard({
      id: crypto.randomUUID(),
      file_id: "",
      url: null,
      uploading: false,
      progress: 0,
    });
    setExistingFileIds((prev) => {
      const copy = { ...prev };
      delete copy.picture_card_db_id;
      return copy;
    });
  };

  // ── BOOK audio slots ─────────────────────────────────────────────────────
  // Emptying the slot is the whole deletion: buildFilesArrayForUpdate omits
  // empty slots and the backend soft-deletes every ResourceFile row missing
  // from the payload. The title lives on that same row, so it goes with it.
  const handleRemoveExtraFile = () => {
    setinitialValues((prev) => ({
      ...prev,
      extra_file_id: "",
      extra_file_id_related_field: "",
    }));
    setUploadedExtraFile("");
    setExtraFileUrl(null);
    setExistingFileIds((prev) => {
      const copy = { ...prev };
      delete copy.extra_file_db_id;
      return copy;
    });
  };

  const handleRemoveAdditionalExtraFile = () => {
    setinitialValues((prev) => ({
      ...prev,
      additional_extra_file_id: "",
      additional_extra_file_id_related_field: "",
    }));
    setUploadedAdditionalExtraFile("");
    setAdditionalExtraFileUrl(null);
    setExistingFileIds((prev) => {
      const copy = { ...prev };
      delete copy.additional_extra_file_db_id;
      return copy;
    });
  };

  // ── DIAGNOSTIC Word copy ─────────────────────────────────────────────────
  // Same deletion contract as the BOOK audio slots: the backend soft-deletes
  // the row once it is missing from the PATCH payload.
  const handleRemoveDiagnosticStudentEditable = () => {
    setinitialValues((prev) => ({
      ...prev,
      diagnostic_student_editable_file_id: "",
    }));
    setUploadedDiagnosticStudentEditable("");
    setDiagnosticStudentEditableUrl(null);
    setExistingFileIds((prev) => {
      const copy = { ...prev };
      delete copy.diagnostic_student_editable_db_id;
      return copy;
    });
  };

  // ── LITERACY_WORKSHEET handlers ──────────────────────────────────────────
  const handleAddWorksheetBlock = () => {
    setWorksheetBlocks((prev) => [...prev, emptyWorksheetBlock()]);
  };

  const handleRemoveWorksheetBlock = (uiId: string) => {
    setWorksheetBlocks((prev) => prev.filter((b) => b.uiId !== uiId));
  };

  const handleWorksheetBlockTitleChange = (uiId: string, title: string) => {
    setWorksheetBlocks((prev) =>
      prev.map((b) => (b.uiId === uiId ? { ...b, title } : b)),
    );
  };

  const handleWorksheetFileUpload = async (
    uiId: string,
    fileType: WorksheetFileType,
    options: any,
  ) => {
    setWorksheetBlocks((prev) =>
      prev.map((b) =>
        b.uiId === uiId
          ? {
              ...b,
              files: b.files.map((f) =>
                f.fileType === fileType
                  ? { ...f, uploading: true, progress: 0 }
                  : f,
              ),
            }
          : b,
      ),
    );

    const optionsWithProgress = {
      ...options,
      onProgress: ({ percent }: { percent: number }) => {
        setWorksheetBlocks((prev) =>
          prev.map((b) =>
            b.uiId === uiId
              ? {
                  ...b,
                  files: b.files.map((f) =>
                    f.fileType === fileType
                      ? { ...f, progress: Math.round(percent) }
                      : f,
                  ),
                }
              : b,
          ),
        );
        options.onProgress?.({ percent });
      },
    };

    try {
      const result = await customUpload(optionsWithProgress);
      if ("fileName" in result && result.fileName && result.url) {
        setWorksheetBlocks((prev) =>
          prev.map((b) =>
            b.uiId === uiId
              ? {
                  ...b,
                  files: b.files.map((f) =>
                    f.fileType === fileType
                      ? {
                          ...f,
                          file_id: result.fileName,
                          url: result.url,
                          uploading: false,
                          progress: 100,
                        }
                      : f,
                  ),
                }
              : b,
          ),
        );
      } else {
        setWorksheetBlocks((prev) =>
          prev.map((b) =>
            b.uiId === uiId
              ? {
                  ...b,
                  files: b.files.map((f) =>
                    f.fileType === fileType
                      ? { ...f, uploading: false, progress: 0 }
                      : f,
                  ),
                }
              : b,
          ),
        );
      }
    } catch (error) {
      console.error(`Error uploading worksheet file ${fileType}:`, error);
      setWorksheetBlocks((prev) =>
        prev.map((b) =>
          b.uiId === uiId
            ? {
                ...b,
                files: b.files.map((f) =>
                  f.fileType === fileType
                    ? { ...f, uploading: false, progress: 0 }
                    : f,
                ),
              }
            : b,
        ),
      );
    }
  };

  const handleWorksheetFileRemove = (
    uiId: string,
    fileType: WorksheetFileType,
  ) => {
    setWorksheetBlocks((prev) =>
      prev.map((b) =>
        b.uiId === uiId
          ? {
              ...b,
              files: b.files.map((f) =>
                f.fileType === fileType
                  ? { ...f, file_id: "", url: null, dbId: undefined }
                  : f,
              ),
            }
          : b,
      ),
    );
  };
  // ────────────────────────────────────────────────────────────────────────

  // Handler para subir la versión iOS del juego
  const handleCustomUploadIos = async (options: any) => {
    setUploadingIos(true);
    setProgressIos(0);

    const optionsWithProgress = {
      ...options,
      onProgress: ({ percent }: { percent: number }) => {
        setProgressIos(Math.round(percent));
        if (options.onProgress) {
          options.onProgress({ percent });
        }
      },
      setProcessing: setProcessingIos,
    };

    try {
      const result = await uploadGameArchive(optionsWithProgress, "ios");

      if ("gameEntryPoint" in result && result?.gameEntryPoint) {
        setinitialValues({
          ...initialValues,
          file_id_ios: result.gameEntryPoint,
        });
        setUploadedFilenameIos(result.gameEntryPoint);
        setFileUrlIos(result.gameEntryPoint);
      } else {
        console.warn("Resultado incompleto para iOS game:", result);
      }
    } catch (error) {
      console.error("Error en la subida del juego iOS:", error);
    } finally {
      setUploadingIos(false);
    }
  };

  const saveReadingContent = async (
    resId: number,
    draft: ReadingContentDraft,
    existing: ReadingContent | null,
  ) => {
    // Build blocks array from pages
    const allBlocks = draft.pages.flatMap((page) =>
      page.blocks.map((block, idx) => ({
        ...block,
        pageNumber: page.pageNumber,
        order: idx + 1,
      })),
    );

    // Full-array inline sync: always send the complete desired activities
    // array (backend diffs by id, soft-deletes removed ones). Legacy
    // `activity` string is intentionally omitted — activities[] is now the
    // source of truth.
    const activitiesDto = draft.activities.map((a, idx) => ({
      id: a.id,
      title: a.title || undefined,
      content: a.content,
      order: idx,
    }));

    if (!existing) {
      // CREATE reading content with all blocks
      const blocksDto = allBlocks.map((b, globalIdx) =>
        buildReadingBlockDto(b, globalIdx + 1, b.pageNumber),
      );

      await createReadingContent({
        resource_id: resId,
        title: draft.title,
        mode: draft.mode,
        cover_url: draft.coverUrl || undefined,
        author: draft.author || undefined,
        illustrator: draft.illustrator || undefined,
        genre: draft.genre || undefined,
        estimated_reading_time_minutes:
          draft.estimatedReadingTimeMinutes || undefined,
        reading_type: draft.readingType || undefined,
        level: draft.level || undefined,
        extended_credits: draft.extendedCredits?.trim() || undefined,
        activities: activitiesDto,
        blocks: blocksDto,
      });
    } else {
      // UPDATE existing reading content metadata
      await updateReadingContentApi(existing.id, {
        title: draft.title,
        mode: draft.mode,
        cover_url: draft.coverUrl || undefined,
        author: draft.author || undefined,
        illustrator: draft.illustrator || undefined,
        genre: draft.genre || undefined,
        estimated_reading_time_minutes:
          draft.estimatedReadingTimeMinutes || undefined,
        reading_type: draft.readingType || undefined,
        level: draft.level || undefined,
        // `null` (no undefined) para que vaciar el campo borre los créditos.
        extended_credits: draft.extendedCredits?.trim() || null,
        activities: activitiesDto,
      });

      // Sync blocks: figure out which to add, update, delete
      const existingBlockIds = new Set(existing.blocks.map((b) => b.id));
      const draftBlockIds = new Set(
        allBlocks.filter((b) => b.id).map((b) => b.id!),
      );

      // Delete removed blocks
      for (const existingBlock of existing.blocks) {
        if (!draftBlockIds.has(existingBlock.id)) {
          await deleteBlock(existing.id, existingBlock.id);
        }
      }

      // Add new blocks and update existing ones
      const blockIdsForReorder: number[] = [];
      for (let i = 0; i < allBlocks.length; i++) {
        const b = allBlocks[i];
        if (b.id && existingBlockIds.has(b.id)) {
          // Update existing
          await updateBlock(
            existing.id,
            b.id,
            buildReadingBlockDto(b, i + 1, b.pageNumber),
          );
          blockIdsForReorder.push(b.id);
        } else {
          // Add new
          const newBlock = await addBlock(
            existing.id,
            buildReadingBlockDto(b, i + 1, b.pageNumber),
          );
          if (newBlock) {
            blockIdsForReorder.push(newBlock.id);
          }
        }
      }

      // Reorder if we have block ids
      if (blockIdsForReorder.length > 1) {
        await reorderBlocks(existing.id, {
          block_ids: blockIdsForReorder,
        });
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (
      initialValues.type === ResourceType.GAME_QUIZ &&
      !initialValues.game_quiz_id
    ) {
      notify(
        "error",
        "Debes seleccionar una Quizz Emmi para crear este recurso.",
        "Error",
      );
      setLoading(false);
      return;
    }

    if (initialValues.type === ResourceType.BOOK) {
      if (!initialValues.file_id && !initialValues.student_book_file_id) {
        notify(
          "error",
          "Debes subir al menos un libro (maestro o alumno).",
          "Error",
        );
        setLoading(false);
        return;
      }
    }

    // Validación para DIAGNOSTIC: el examen se publica por audiencia, así que
    // basta con una de las dos versiones para poder guardar.
    if (initialValues.type === ResourceType.DIAGNOSTIC) {
      if (
        !initialValues.diagnostic_student_file_id &&
        !initialValues.diagnostic_teacher_file_id
      ) {
        notify(
          "error",
          "Debes subir al menos una versión del examen (alumno o maestra).",
          "Error",
        );
        setLoading(false);
        return;
      }
    }

    // Validación para DOWNLOAD
    if (initialValues.type === ResourceType.DOWNLOAD) {
      if (pdfFiles.length === 0 || !pdfFiles.some((pdf) => pdf.file_id)) {
        notify(
          "error",
          "Debes subir al menos un PDF para recursos de tipo Descarga.",
          "Error",
        );
        setLoading(false);
        return;
      }
    }

    // Validación para Lectura Kinder: requiere PDF
    if (
      initialValues.type === ResourceType.READING &&
      readingVariant === "kinder" &&
      !initialValues.file_id
    ) {
      notify(
        "error",
        "Debes subir un archivo PDF para Lectura Kinder.",
        "Error",
      );
      setLoading(false);
      return;
    }

    // Validación para LITERACY_WORKSHEET
    if (initialValues.type === ResourceType.LITERACY_WORKSHEET) {
      if (worksheetBlocks.length === 0) {
        notify("error", "Debes agregar al menos un bloque de fichas.", "Error");
        setLoading(false);
        return;
      }
      const invalidBlock = worksheetBlocks.find(
        (b) => !b.files.some((f) => f.file_id),
      );
      if (invalidBlock) {
        notify(
          "error",
          "Cada bloque debe tener al menos un PDF subido.",
          "Error",
        );
        setLoading(false);
        return;
      }
    }

    // Validación para otros tipos de recursos (excepto QUIZ, SLIDES, DOWNLOAD y READING primaria)
    if (
      initialValues.type !== ResourceType.QUIZ &&
      initialValues.type !== ResourceType.GAME_QUIZ &&
      initialValues.type !== ResourceType.SLIDES &&
      initialValues.type !== ResourceType.BOOK &&
      initialValues.type !== ResourceType.DOWNLOAD &&
      initialValues.type !== ResourceType.LITERACY_WORKSHEET &&
      initialValues.type !== ResourceType.DIAGNOSTIC &&
      !(initialValues.type === ResourceType.READING) &&
      !initialValues.file_id
    ) {
      notify(
        "error",
        "Debes subir un archivo si el recurso lo requiere.",
        "Error",
      );
      setLoading(false);
      return;
    }

    // Construir files array según si es CREATE o UPDATE
    const filesArray = resourceId
      ? buildFilesArrayForUpdate(
          initialValues.type,
          initialValues.file_id || "",
          initialValues.extra_file_id || "",
          initialValues.additional_extra_file_id || "",
          initialValues.student_book_file_id || "",
          initialValues.extra_file_id_related_field || "",
          initialValues.additional_extra_file_id_related_field || "",
          existingFileIds,
          pdfFiles,
          pictureCard.file_id,
          initialValues.file_id_ios || "",
          readingVariant,
          initialValues.diagnostic_teacher_file_id || "",
          initialValues.diagnostic_student_file_id || "",
          initialValues.diagnostic_student_editable_file_id || "",
        )
      : buildFilesArray(
          initialValues.type,
          initialValues.file_id || "",
          initialValues.extra_file_id || "",
          initialValues.additional_extra_file_id || "",
          initialValues.student_book_file_id || "",
          initialValues.extra_file_id_related_field || "",
          initialValues.additional_extra_file_id_related_field || "",
          pdfFiles,
          pictureCard.file_id,
          initialValues.file_id_ios || "",
          readingVariant,
          initialValues.diagnostic_teacher_file_id || "",
          initialValues.diagnostic_student_file_id || "",
          initialValues.diagnostic_student_editable_file_id || "",
        );

    // Construir payload con nuevo formato (sin campos legacy)
    const payload: any = {
      title: initialValues.title,
      cover: initialValues.cover,
      is_secondary:
        initialValues.type === ResourceType.BOOK
          ? (initialValues.is_secondary ?? false)
          : false,
      type: initialValues.type,
      files: filesArray,
      additional_info: initialValues.additional_info,
      genially_edit_url:
        initialValues.genially_edit_url &&
        initialValues.genially_edit_url.trim()
          ? initialValues.genially_edit_url.trim()
          : null,
      script_doc_url:
        initialValues.script_doc_url && initialValues.script_doc_url.trim()
          ? initialValues.script_doc_url.trim()
          : null,
      drive_url:
        initialValues.drive_url && initialValues.drive_url.trim()
          ? initialValues.drive_url.trim()
          : null,
      ...(initialValues.game_quiz_id !== undefined
        ? { game_quiz_id: initialValues.game_quiz_id }
        : {}),
    };

    // LITERACY_WORKSHEET: add worksheetBlocks, no files array.
    // Always send the complete current state — the backend diffs:
    //   - blocks with id    → update title
    //   - blocks without id → create
    //   - blocks absent     → soft-delete
    //   - file_type present → create or replace
    //   - file_type absent  → soft-delete
    if (initialValues.type === ResourceType.LITERACY_WORKSHEET) {
      payload.files = [];
      payload.worksheetBlocks = worksheetBlocks.map((block) => ({
        ...(block.id ? { id: block.id } : {}),
        title: block.title,
        files: block.files
          .filter((f) => f.file_id)
          .map((f) => ({
            file_type: f.fileType,
            file_id: f.file_id,
          })),
      }));
    }

    // For READING primaria: save resource first, then reading content, then navigate
    if (
      initialValues.type === ResourceType.READING &&
      readingVariant === "primaria" &&
      readingContentDraftRef.current
    ) {
      dispatch({ type: "resource/setLoading", payload: true });
      try {
        let savedResourceId: number;
        if (resourceId) {
          const response = await updateResource(resourceId, payload);
          if (response?.id) {
            dispatch({
              type: "resource/updateResource",
              payload: { id: resourceId, data: response },
            });
            savedResourceId = resourceId;
          } else {
            throw new Error("Update failed");
          }
        } else {
          const response = await createResource(payload);
          if (response?.id) {
            dispatch({
              type: "resource/addResource",
              payload: response,
            });
            savedResourceId = response.id;
          } else {
            throw new Error("Create failed");
          }
        }

        // Sync main form title and cover into the reading content draft
        const enrichedDraft: ReadingContentDraft = {
          ...readingContentDraftRef.current,
          title: initialValues.title,
          coverUrl:
            coverUrl ||
            uploadedCover ||
            readingContentDraftRef.current.coverUrl,
        };

        // Save reading content
        await saveReadingContent(
          savedResourceId,
          enrichedDraft,
          existingReadingContent,
        );

        notify("success", "Recurso y contenido de lectura guardados.", "Exito");
        navigate(-1);
      } catch (error: any) {
        console.error("Error saving reading resource:", error);
        notify(
          "error",
          error?.response?.data?.message ||
            "Ocurrio un error al guardar el recurso.",
          "Error",
        );
      } finally {
        dispatch({ type: "resource/setLoading", payload: false });
        setLoading(false);
      }
    } else {
      await handleSubmitFormResponseManage(
        payload,
        createResource,
        updateResource,
        navigate,
        dispatch,
        "resource",
        "addResource",
        "updateResource",
        resourceId,
      );
      setLoading(false);
    }
  };

  if (loading || loadingGameQuizzes) {
    return <ActivityIndicator />;
  }

  // El backend resuelve estos covers por asignatura, así que el form no los pide.
  const usesAutomaticCover =
    initialValues.type === ResourceType.DIAGNOSTIC ||
    (initialValues.type === ResourceType.BOOK && !initialValues.is_secondary);

  return (
    <Form
      initialValues={initialValues}
      layout="vertical"
      onFinish={handleSubmit}
      className="resource-form-container"
    >
      <Title level={3}>{resourceId ? "Editar Recurso" : "Crear Recurso"}</Title>
      {resourceId && (
        <LastModified entityType="resources" entityId={resourceId} />
      )}

      <Tabs
        defaultActiveKey="data"
        items={[
          {
            key: "data",
            label: "Datos",
            children: (
              <>
      <Form.Item
        label="Título"
        name="title"
                  rules={[
                    { required: true, message: "Por favor ingresa un título" },
                  ]}
      >
        <Input
          placeholder="Título del recurso"
          value={initialValues.title}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setinitialValues({
              ...initialValues,
              title: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item label="Tipo de recurso" name="type">
        <Select
          placeholder="Selecciona un tipo"
          value={toDropdownValue(initialValues.type, readingVariant)}
          onChange={(value: ResourceTypeDropdownValue) => {
                      const { type, readingVariant: variant } =
                        parseDropdownValue(value);
            setinitialValues({
              ...initialValues,
              type,
            });
            setReadingVariant(variant);
            // Reset reading content when switching away from primaria
            if (variant !== "primaria") {
              readingContentDraftRef.current = null;
              setExistingReadingContent(null);
            }
          }}
        >
          {getResourceTypeOptions().map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* SCRUM-620: un curso puede tener varios libros. Los principales usan el
          cover estándar de su asignatura (lo resuelve el backend); los
          secundarios suben el suyo. */}
      {initialValues.type === ResourceType.BOOK && (
        <Form.Item label="Tipo de libro">
          <Checkbox
            checked={initialValues.is_secondary ?? false}
            onChange={(e) =>
              setinitialValues({
                ...initialValues,
                is_secondary: e.target.checked,
              })
            }
          >
            Libro secundario
          </Checkbox>
          <p className="uploaded-filename">
            Sin marcar es libro principal: usa el cover estándar de la
            asignatura. Los secundarios suben su propio cover.
          </p>
        </Form.Item>
      )}

      {/* DIAGNOSTIC covers are picked by the app from the course subject, so the
          form does not ask for one. */}
      {usesAutomaticCover && (
        <Form.Item label="Cover">
          <p className="uploaded-filename">
            El cover se asigna automáticamente según la asignatura del curso.
          </p>
        </Form.Item>
      )}

      {!usesAutomaticCover && (
      <Form.Item
        label="Cover"
        validateStatus={!initialValues.cover ? "error" : ""}
      >
        <>
          <Upload
                      customRequest={(options) =>
                        handleCustomUpload(options, "cover")
                      }
            showUploadList={false}
            accept={ACCEPTED_IMAGE_MIME_TYPES}
            beforeUpload={(file) => {
              if (file.size > MAX_UPLOAD_IMAGE_SIZE_BYTES) {
                message.error("La imagen supera el tamaño máximo de 25 MB");
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
            disabled={uploadingCover}
          >

                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingCover}
                      >
              {uploadedCover ? "Reemplazar cover" : "Subir cover"}
            </Button>
          </Upload>
                    {uploadingCover && (
                      <Progress percent={progress} size="small" />
                    )}
          {uploadedCover && (
            <p className="uploaded-filename">📄 {uploadedCover}</p>
          )}
        </>
      </Form.Item>
      )}

      {coverUrl && uploadedCover && (
        <>
          <ResourcePreview
            fileId={uploadedCover}
            fileUrl={coverUrl}
            type={initialValues.type}
          />
          <Divider />
        </>
      )}

      {initialValues.type !== ResourceType.QUIZ &&
        initialValues.type !== ResourceType.GAME_QUIZ &&
        initialValues.type !== ResourceType.BOOK &&
        initialValues.type !== ResourceType.DOWNLOAD &&
        initialValues.type !== ResourceType.LITERACY_WORKSHEET &&
        initialValues.type !== ResourceType.DIAGNOSTIC &&
        !(
          initialValues.type === ResourceType.READING &&
          readingVariant === "primaria"
        ) && (
          <>
            <Form.Item
              label="Archivo"
              validateStatus={!initialValues.file_id ? "error" : ""}
              help={
                !initialValues.file_id &&
                initialValues.type !== ResourceType.SLIDES
                  ? "Debes subir un archivo si el recurso lo requiere."
                  : ""
              }
            >
              <>
                <Upload
                  customRequest={(options) =>
                    handleCustomUpload(options, "file_id")
                  }
                  showUploadList={false}
                  disabled={uploading}
                >
                            <Button
                              icon={<UploadOutlined />}
                              loading={uploading}
                            >
                              {uploadedFilename
                                ? "Reemplazar archivo"
                                : "Subir archivo"}
                  </Button>
                </Upload>

                          {uploading && (
                            <Progress percent={progress} size="small" />
                          )}

                          {processing &&
                            initialValues.type === ResourceType.GAME && (
                  <div style={{ marginTop: 8 }}>
                    <Spin tip="Procesando el archivo, esto puede demorar varios minutos, por favor espera...">
                      <div style={{ height: 40 }} />
                    </Spin>
                  </div>
                )}

                {uploadedFilename &&
                  initialValues.type !== ResourceType.GAME && (
                              <p className="uploaded-filename">
                                📄 {uploadedFilename}
                              </p>
                  )}
              </>
            </Form.Item>

            <>
              {fileUrl && uploadedFilename && (
                <>
                  <ResourcePreview
                    fileId={uploadedFilename}
                    fileUrl={fileUrl}
                    type={initialValues.type}
                  />
                  <Divider />
                </>
              )}

              {/* Si es un juego y se detectó versión optimizada, mostrarla */}
                        {initialValues.type === ResourceType.GAME &&
                          optimizedFileUrl && (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Versión optimizada</strong>
                  </div>
                  <ResourcePreview
                    fileId={uploadedFilename}
                    fileUrl={optimizedFileUrl}
                    type={initialValues.type}
                  />
                  <Divider />
                </>
              )}

              <br />
            </>
          </>
        )}

      {initialValues.type === ResourceType.VIDEO && (
        <Form.Item
          label="URL del documento de guión (Google Doc)"
          name="script_doc_url"
          rules={[
            {
              pattern: /^https:\/\/(docs|drive)\.google\.com\/.+/i,
              message:
                "Debe ser un URL de Google Docs o Google Drive (https://docs.google.com/... o https://drive.google.com/...)",
            },
            {
              max: 1024,
              message: "El URL no puede superar los 1024 caracteres.",
            },
          ]}
        >
          <Input
            placeholder="https://docs.google.com/document/d/..."
            value={initialValues.script_doc_url ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setinitialValues({
                ...initialValues,
                script_doc_url: e.target.value,
              })
            }
          />
        </Form.Item>
      )}

      {initialValues.type === ResourceType.GAME && (
        <>
          <Form.Item label="Archivo iOS (opcional)">
            <>
              <Upload
                          customRequest={(options) =>
                            handleCustomUploadIos(options)
                          }
                showUploadList={false}
                disabled={uploadingIos}
                accept=".zip"
              >
                          <Button
                            icon={<UploadOutlined />}
                            loading={uploadingIos}
                          >
                  {uploadedFilenameIos
                    ? "Reemplazar archivo iOS"
                    : "Subir archivo iOS"}
                </Button>
              </Upload>

                        {uploadingIos && (
                          <Progress percent={progressIos} size="small" />
                        )}

              {processingIos && (
                <div style={{ marginTop: 8 }}>
                  <Spin tip="Procesando el archivo iOS, esto puede demorar varios minutos, por favor espera...">
                    <div style={{ height: 40 }} />
                  </Spin>
                </div>
              )}
            </>
          </Form.Item>

          {fileUrlIos && uploadedFilenameIos && (
            <>
              <ResourcePreview
                fileId={uploadedFilenameIos}
                fileUrl={fileUrlIos}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}
        </>
      )}

      {initialValues.type === ResourceType.AUDIO && (
        <>
          <Form.Item
            label="Archivo extra"
                      validateStatus={
                        !initialValues.extra_file_id ? "error" : ""
                      }
          >
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "extra_file_id")
                }
                showUploadList={false}
                disabled={extraFileUploading}
              >
                          <Button
                            icon={<UploadOutlined />}
                            loading={extraFileUploading}
                          >
                            {uploadedExtraFile
                              ? "Reemplazar archivo"
                              : "Subir archivo"}
                </Button>
              </Upload>
              {extraFileUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedExtraFile && (
                          <p className="uploaded-filename">
                            📄 {uploadedExtraFile}
                          </p>
              )}
            </>
          </Form.Item>

          {extraFileUrl && uploadedExtraFile && (
            <>
              <ResourcePreview
                fileId={uploadedExtraFile}
                fileUrl={extraFileUrl}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}
        </>
      )}

      {initialValues.type === ResourceType.DOWNLOAD && (
        <>
          {/* Editors comment on the Drive copy of the PDF (SCRUM-1530); the
              HUB download fills it in and it can be fixed here. */}
          <Form.Item
            label="URL del PDF en Drive"
            name="drive_url"
            rules={[
              {
                pattern: /^\s*https:\/\/(docs|drive)\.google\.com\/.+/i,
                message:
                  "Debe ser un URL de Google Drive (https://drive.google.com/...)",
              },
              {
                max: 1024,
                message: "El URL no puede superar los 1024 caracteres.",
              },
            ]}
          >
            <Input
              placeholder="https://drive.google.com/file/d/..."
              value={initialValues.drive_url ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setinitialValues({
                  ...initialValues,
                  drive_url: e.target.value,
                })
              }
            />
          </Form.Item>
          <Form.Item
            label="Archivos PDF"
            validateStatus={
                        pdfFiles.length === 0 ||
                        !pdfFiles.some((pdf) => pdf.file_id)
                ? "error"
                : ""
            }
            help={
                        pdfFiles.length === 0 ||
                        !pdfFiles.some((pdf) => pdf.file_id)
                ? "Debes subir al menos un PDF."
                : ""
            }
          >
            {pdfFiles.map((pdf, index) => (
              <PdfItem
                key={pdf.id}
                pdf={pdf}
                index={index}
                onUpload={handlePdfUpload}
                onRemove={handleRemovePdf}
                progress={pdf.progress ?? 0}
              />
            ))}

            {/* picture card single uploader */}
            <PdfItem
              key={pictureCard.id}
              pdf={pictureCard}
              index={0}
              title="Picture Cards"
                        onUpload={(_id, options) =>
                          handlePictureCardUpload(options)
                        }
              onRemove={() => handleRemovePictureCard()}
              progress={pictureCard.progress ?? 0}
            />

            <Button
              type="dashed"
              onClick={handleAddPdf}
              icon={<PlusOutlined />}
              block
                        style={{
                          marginTop: pdfFiles.length > 0 ? "16px" : "0",
                        }}
            >
              Agregar otro PDF
            </Button>
          </Form.Item>

          {pdfFiles.length > 0 && <Divider />}
        </>
      )}

      {initialValues.type === ResourceType.DIAGNOSTIC && (
        <>
          <Form.Item
            label="Examen versión alumno"
            validateStatus={
              !initialValues.diagnostic_student_file_id &&
              !initialValues.diagnostic_teacher_file_id
                ? "error"
                : ""
            }
            help={
              !initialValues.diagnostic_student_file_id &&
              !initialValues.diagnostic_teacher_file_id
                ? "Debes subir al menos una versión del examen (alumno o maestra)."
                : ""
            }
          >
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "diagnostic_student_file_id")
                }
                showUploadList={false}
                disabled={diagnosticStudentUploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={diagnosticStudentUploading}
                >
                  {uploadedDiagnosticStudent
                    ? "Reemplazar archivo"
                    : "Subir archivo"}
                </Button>
              </Upload>
              {diagnosticStudentUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedDiagnosticStudent && (
                <p className="uploaded-filename">
                  📄 {uploadedDiagnosticStudent}
                </p>
              )}
            </>
          </Form.Item>

          {diagnosticStudentUrl && uploadedDiagnosticStudent && (
            <>
              <ResourcePreview
                fileId={uploadedDiagnosticStudent}
                fileUrl={diagnosticStudentUrl}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}

          <Form.Item
            label="Examen versión alumno editable (Word, opcional)"
            extra="El maestro lo descarga con «Descargar editable» en la versión alumno del examen."
          >
            <>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <Upload
                  accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  customRequest={(options) =>
                    handleCustomUpload(
                      options,
                      "diagnostic_student_editable_file_id",
                    )
                  }
                  showUploadList={false}
                  disabled={diagnosticStudentEditableUploading}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={diagnosticStudentEditableUploading}
                  >
                    {uploadedDiagnosticStudentEditable
                      ? "Reemplazar Word"
                      : "Subir Word"}
                  </Button>
                </Upload>

                {uploadedDiagnosticStudentEditable && (
                  <Popconfirm
                    title="¿Estás seguro de eliminar el Word editable?"
                    onConfirm={handleRemoveDiagnosticStudentEditable}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={diagnosticStudentEditableUploading}
                    >
                      Eliminar Word
                    </Button>
                  </Popconfirm>
                )}
              </div>
              {diagnosticStudentEditableUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedDiagnosticStudentEditable && (
                <p className="uploaded-filename">
                  📝{" "}
                  {diagnosticStudentEditableUrl ? (
                    <a
                      href={diagnosticStudentEditableUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {uploadedDiagnosticStudentEditable}
                    </a>
                  ) : (
                    uploadedDiagnosticStudentEditable
                  )}
                </p>
              )}
            </>
          </Form.Item>
          <Divider />

          <Form.Item label="Examen versión maestra">
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "diagnostic_teacher_file_id")
                }
                showUploadList={false}
                disabled={diagnosticTeacherUploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={diagnosticTeacherUploading}
                >
                  {uploadedDiagnosticTeacher
                    ? "Reemplazar archivo"
                    : "Subir archivo"}
                </Button>
              </Upload>
              {diagnosticTeacherUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedDiagnosticTeacher && (
                <p className="uploaded-filename">
                  📄 {uploadedDiagnosticTeacher}
                </p>
              )}
            </>
          </Form.Item>

          {diagnosticTeacherUrl && uploadedDiagnosticTeacher && (
            <>
              <ResourcePreview
                fileId={uploadedDiagnosticTeacher}
                fileUrl={diagnosticTeacherUrl}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}
        </>
      )}

      {initialValues.type === ResourceType.BOOK && (
        <>
          <Form.Item
            label="Libro maestro"
            validateStatus={
                        !initialValues.file_id &&
                        !initialValues.student_book_file_id
                ? "error"
                : ""
            }
            help={
                        !initialValues.file_id &&
                        !initialValues.student_book_file_id
                ? "Debes subir al menos un libro (maestro o alumno)."
                : ""
            }
          >
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "file_id")
                }
                showUploadList={false}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                            {uploadedFilename
                              ? "Reemplazar archivo"
                              : "Subir archivo"}
                </Button>
              </Upload>

                        {uploading && (
                          <Progress percent={progress} size="small" />
                        )}

              {uploadedFilename && (
                          <p className="uploaded-filename">
                            📄 {uploadedFilename}
                          </p>
              )}
            </>
          </Form.Item>

          {fileUrl && uploadedFilename && (
            <>
              <ResourcePreview
                fileId={uploadedFilename}
                fileUrl={fileUrl}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}

          <Form.Item
            label="Libro alumno"
            validateStatus={
                        !initialValues.file_id &&
                        !initialValues.student_book_file_id
                ? "error"
                : ""
            }
            help={
                        !initialValues.file_id &&
                        !initialValues.student_book_file_id
                ? "Debes subir al menos un libro (maestro o alumno)."
                : ""
            }
          >
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "student_book_file_id")
                }
                showUploadList={false}
                disabled={studentBookUploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={studentBookUploading}
                >
                            {uploadedStudentBook
                              ? "Reemplazar archivo"
                              : "Subir archivo"}
                </Button>
              </Upload>
              {studentBookUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedStudentBook && (
                          <p className="uploaded-filename">
                            📄 {uploadedStudentBook}
                          </p>
              )}
            </>
          </Form.Item>

          {studentBookUrl && uploadedStudentBook && (
            <>
              <ResourcePreview
                fileId={uploadedStudentBook}
                fileUrl={studentBookUrl}
                type={initialValues.type}
              />
              <Divider />
            </>
          )}

          <Form.Item label="Audio 1" name="extra_file_id">
            <>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <Upload
                  customRequest={(options) =>
                    handleCustomUpload(options, "extra_file_id")
                  }
                  showUploadList={false}
                  disabled={extraFileUploading}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={extraFileUploading}
                  >
                    {uploadedExtraFile
                      ? "Reemplazar Audio 1"
                      : "Subir Audio 1"}
                  </Button>
                </Upload>

                {uploadedExtraFile && (
                  <Popconfirm
                    title="¿Estás seguro de eliminar este audio?"
                    onConfirm={handleRemoveExtraFile}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={extraFileUploading}
                    >
                      Eliminar Audio 1
                    </Button>
                  </Popconfirm>
                )}
              </div>
              {extraFileUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedExtraFile && (
                          <p className="uploaded-filename">
                            🎵 {uploadedExtraFile}
                          </p>
              )}
            </>
          </Form.Item>

          {/* No `name` on purpose: a registered Form.Item overrides the child's
              value with antd's own store, so clearing the title on delete
              wouldn't show. initialValues is the single source of truth — it is
              what handleSubmit sends. */}
          <Form.Item
            label="Título Audio 1 (opcional)"
            htmlFor="extra_file_id_related_field"
          >
            <Input
              id="extra_file_id_related_field"
              placeholder="Título para el primer audio"
              value={initialValues.extra_file_id_related_field}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setinitialValues({
                  ...initialValues,
                  extra_file_id_related_field: e.target.value,
                })
              }
            />
          </Form.Item>

          {extraFileUrl && uploadedExtraFile && (
            <>
              <ResourcePreview
                fileId={uploadedExtraFile}
                fileUrl={extraFileUrl}
                type={ResourceType.AUDIO}
              />
              <Divider />
            </>
          )}

          <Form.Item label="Audio 2" name="additional_extra_file_id">
            <>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <Upload
                  customRequest={(options) =>
                    handleCustomUpload(options, "additional_extra_file_id")
                  }
                  showUploadList={false}
                  disabled={additionalExtraFileUploading}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={additionalExtraFileUploading}
                  >
                    {uploadedAdditionalExtraFile
                      ? "Reemplazar Audio 2"
                      : "Subir Audio 2"}
                  </Button>
                </Upload>

                {uploadedAdditionalExtraFile && (
                  <Popconfirm
                    title="¿Estás seguro de eliminar este audio?"
                    onConfirm={handleRemoveAdditionalExtraFile}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={additionalExtraFileUploading}
                    >
                      Eliminar Audio 2
                    </Button>
                  </Popconfirm>
                )}
              </div>
              {additionalExtraFileUploading && (
                <Progress percent={progress} size="small" />
              )}
              {uploadedAdditionalExtraFile && (
                <p className="uploaded-filename">
                  🎵 {uploadedAdditionalExtraFile}
                </p>
              )}
            </>
          </Form.Item>

          <Form.Item
            label="Título Audio 2 (opcional)"
            htmlFor="additional_extra_file_id_related_field"
          >
            <Input
              id="additional_extra_file_id_related_field"
              placeholder="Título para el segundo audio"
                        value={
                          initialValues.additional_extra_file_id_related_field
                        }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setinitialValues({
                  ...initialValues,
                            additional_extra_file_id_related_field:
                              e.target.value,
                })
              }
            />
          </Form.Item>

          {additionalExtraFileUrl && uploadedAdditionalExtraFile && (
            <>
              <ResourcePreview
                fileId={uploadedAdditionalExtraFile}
                fileUrl={additionalExtraFileUrl}
                type={ResourceType.AUDIO}
              />
              <Divider />
            </>
          )}
        </>
      )}

      {(initialValues.type === ResourceType.GAME_QUIZ ||
        initialValues.type === ResourceType.READING ||
        initialValues.type === ResourceType.VIDEO) && (
        <Form.Item
          label={
            initialValues.type === ResourceType.GAME_QUIZ
                        ? "Quizzes Emmi asociados"
                        : "Quizzes Emmi asociados (opcional)"
          }
          name="game_quiz_id"
          rules={
            initialValues.type === ResourceType.GAME_QUIZ
              ? [
                  {
                    required: true,
                              message: "Debes seleccionar un Quiz Emmi",
                  },
                ]
              : []
          }
          help={
            initialValues.type === ResourceType.GAME_QUIZ
                        ? "Selecciona el Quiz Emmi (debe estar publicado)"
              : initialValues.type === ResourceType.VIDEO
                          ? "Opcional: asocia un Quiz Emmi a este recurso de video"
                          : "Opcional: asocia un Quiz Emmi a este recurso de lectura"
          }
        >
          <Select
                      placeholder="Selecciona un Quiz Emmi"
            value={initialValues.game_quiz_id ?? undefined}
            allowClear
            loading={loadingGameQuizzes}
            showSearch
            optionFilterProp="label"
            onChange={(value: number | null) =>
              setinitialValues({
                ...initialValues,
                          game_quiz_id:
                            value !== null ? Number(value) : undefined,
              })
            }
            options={gameQuizzes.map((gq) => ({
              value: gq.id,
              label: `#${gq.id} — ${gq.title}`,
            }))}
          />
        </Form.Item>
      )}

      {initialValues.type === ResourceType.GAME_QUIZ && (
        <>
          <Form.Item label="Video (opcional)" name="file_id">
            <>
              <Upload
                customRequest={(options) =>
                  handleCustomUpload(options, "file_id")
                }
                showUploadList={false}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                            {uploadedFilename
                              ? "Reemplazar video"
                              : "Subir video"}
                </Button>
              </Upload>

                        {uploading && (
                          <Progress percent={progress} size="small" />
                        )}

              {uploadedFilename && (
                          <p className="uploaded-filename">
                            🎥 {uploadedFilename}
                          </p>
              )}
            </>
          </Form.Item>

          {fileUrl && uploadedFilename && (
            <>
              <ResourcePreview
                fileId={uploadedFilename}
                fileUrl={fileUrl}
                type={ResourceType.VIDEO}
              />
              <Divider />
            </>
          )}
        </>
      )}

      {initialValues.type === ResourceType.READING &&
        readingVariant === "primaria" && (
          <ReadingContentEditor
            resourceId={resourceId || undefined}
            onDataChange={handleReadingDataChange}
            initialData={existingReadingContent}
          />
        )}

      {initialValues.type === ResourceType.LITERACY_WORKSHEET && (
        <>
          <Title level={5}>Fichas de trabajo</Title>

          {worksheetBlocks.map((block, blockIdx) => {
                      const hasAtLeastOnePdf = block.files.some(
                        (f) => f.file_id,
                      );
            return (
              <div
                key={block.uiId}
                style={{
                  border: "1px solid var(--bo-border-strong)",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                  backgroundColor: "var(--bo-inset-bg)",
                }}
              >
                <Form.Item label="Título del PDF">
                  <Input
                    placeholder="Ej: Letra A y a"
                    value={block.title}
                    onChange={(e) =>
                      handleWorksheetBlockTitleChange(
                        block.uiId,
                        e.target.value,
                      )
                    }
                  />
                </Form.Item>

                <p style={{ marginBottom: "8px", fontWeight: 500 }}>
                  Subir al menos uno:
                </p>

                {block.files.map((file) => (
                  <div
                    key={file.fileType}
                    style={{
                      marginBottom: "12px",
                      border: "1px solid var(--bo-card-border)",
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header de sección — siempre visible */}
                    <div
                      style={{
                        padding: "6px 12px",
                        background: "var(--bo-inset-strong-bg)",
                        borderBottom: "1px solid var(--bo-card-border)",
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      {file.label}
                    </div>

                    {file.file_id ? (
                      <>
                        {/* Preview del PDF inline, sin título extra */}
                        <div style={{ padding: "0" }}>
                          <iframe
                            src={`${file.url ?? ""}#toolbar=0&navpanes=0`}
                            style={{
                              width: "100%",
                              height: "300px",
                              border: "none",
                              display: "block",
                            }}
                            title={`Vista previa ${file.label}`}
                          />
                        </div>
                        {file.uploading && (
                          <Progress
                            percent={file.progress}
                            size="small"
                            style={{ padding: "0 12px" }}
                          />
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            padding: "8px 12px",
                          }}
                        >
                          <Upload
                            customRequest={(options) =>
                              handleWorksheetFileUpload(
                                block.uiId,
                                file.fileType,
                                options,
                              )
                            }
                            showUploadList={false}
                            disabled={file.uploading}
                            accept=".pdf"
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={file.uploading}
                            >
                              Sustituir PDF
                            </Button>
                          </Upload>
                          <Popconfirm
                            title={`¿Eliminar el PDF de ${file.label}?`}
                            onConfirm={() =>
                              handleWorksheetFileRemove(
                                block.uiId,
                                file.fileType,
                              )
                            }
                            okText="Sí, eliminar"
                            cancelText="Cancelar"
                          >
                            <Button danger icon={<DeleteOutlined />}>
                              Eliminar PDF
                            </Button>
                          </Popconfirm>
                        </div>
                      </>
                    ) : (
                      <div style={{ padding: "8px 12px" }}>
                        <Upload
                          customRequest={(options) =>
                            handleWorksheetFileUpload(
                              block.uiId,
                              file.fileType,
                              options,
                            )
                          }
                          showUploadList={false}
                          disabled={file.uploading}
                          accept=".pdf"
                          style={{ width: "100%" }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={file.uploading}
                            block
                          >
                            Subir PDF
                          </Button>
                        </Upload>
                        {file.uploading && (
                          <Progress
                            percent={file.progress}
                            size="small"
                            style={{ marginTop: "4px" }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {!hasAtLeastOnePdf && (
                  <p
                    style={{
                      color: "#ff4d4f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    Debes subir al menos un PDF.
                  </p>
                )}

                <Popconfirm
                  title="¿Eliminar este bloque?"
                            onConfirm={() =>
                              handleRemoveWorksheetBlock(block.uiId)
                            }
                  okText="Sí, eliminar"
                  cancelText="Cancelar"
                  disabled={worksheetBlocks.length === 1}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    block
                    style={{ marginTop: "12px" }}
                    disabled={worksheetBlocks.length === 1}
                  >
                    Eliminar bloque
                  </Button>
                </Popconfirm>
              </div>
            );
          })}

          <Button
            type="dashed"
            onClick={handleAddWorksheetBlock}
            icon={<PlusOutlined />}
            block
            style={{ marginBottom: "16px" }}
          >
            Agregar nuevo bloque
          </Button>

                    {worksheetBlocks.some(
                      (b) => !b.files.some((f) => f.file_id),
                    ) && (
            <p
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginBottom: "16px",
              }}
            >
              Cada bloque debe tener al menos un PDF.
            </p>
          )}

          <Divider />
        </>
      )}

      <Form.Item
        label={
          initialValues.type === ResourceType.SLIDES
            ? "URL Genially"
            : "Información adicional"
        }
        name="additional_info"
      >
        <Input.TextArea
          rows={4}
          placeholder="Descripción, detalles extra o algún link hacia algun recurso externo."
          value={initialValues.additional_info}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setinitialValues({
              ...initialValues,
              additional_info: e.target.value,
            })
          }
        />
      </Form.Item>

      {initialValues.type === ResourceType.SLIDES && resourceId && (
        <Form.Item label="Diapositivas">
          <SlidesGallery
            resourceId={resourceId}
            geniallyUrl={
                        (initialValues.additional_info?.match(
                          /https?:\/\/\S+/,
                        ) ?? [])[0] ?? ""
            }
            resourceTitle={initialValues.title}
          />
        </Form.Item>
      )}

      {initialValues.type === ResourceType.SLIDES && (
                  <Form.Item
                    label="Enlace edición de diapositiva"
                    name="genially_edit_url"
                  >
          <Input
            placeholder="Enlace de edición en Genially (opcional)"
            value={initialValues.genially_edit_url}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setinitialValues({
                ...initialValues,
                genially_edit_url: e.target.value,
              })
            }
          />
        </Form.Item>
      )}
              </>
            ),
          },
          ...(resourceId &&
          (initialValues.type === ResourceType.BOOK ||
            initialValues.type === ResourceType.DOWNLOAD ||
            initialValues.type === ResourceType.READING)
            ? [
                {
                  key: "extraction",
                  label: "Extracción de PDFs",
                  children: (
                    <ExtractionTab
                      resourceId={resourceId}
                      job={extractionJob}
                      files={extractionFiles}
                      onRefetch={refetchExtraction}
                    />
                  ),
                },
              ]
            : []),
          ...(resourceId && initialValues.type === ResourceType.VIDEO
            ? [
                {
                  key: "script-extraction",
                  label: "Extracción de guion",
                  children: (
                    <ScriptExtractionTab
                      resourceId={resourceId}
                      scriptExtraction={scriptExtraction}
                      job={
                        // Defensive: when backend exposes `kind`, only pass
                        // SCRIPT jobs to this tab. Otherwise pass through.
                        extractionJob &&
                        extractionJob.kind &&
                        extractionJob.kind !== "SCRIPT"
                          ? null
                          : extractionJob
                      }
                      scriptDocUrl={initialValues.script_doc_url ?? null}
                      onRefetch={refetchExtraction}
                    />
                  ),
                },
              ]
            : []),
          ...(resourceId
            ? [
                {
                  key: "history",
                  label: "Historial",
                  children: (
                    <EntityHistory
                      entityType="resources"
                      entityId={resourceId}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          Guardar recurso
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ResourceForm;
